import styled, { keyframes, css } from "styled-components";
import {
  SPACING,
  QUERIES,
  ANIMATION,
  TOUCH_TARGET,
} from "../../utils/constants";
import { SizeType } from "../../utils";
import { KebabButton } from "../KebabButton";
import { BaseButton } from "../Button";

const fadeInScale = keyframes`
  from {
    opacity: 0;
    transform: translateY(-8px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

export const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

export const CompactTriggerButton = styled(KebabButton)`
  @media (pointer: coarse) {
    min-width: ${TOUCH_TARGET}px;
    min-height: ${TOUCH_TARGET}px;
  }
`;

export const DropdownButton = styled(BaseButton)<{ $size: SizeType }>`
  display: flex;
  align-items: center;
  gap: ${SPACING.xs}px;
  padding: ${SPACING.sm + SPACING.xs}px ${SPACING.sm + SPACING.xs}px;

  ${(props) =>
    props.$size === "sm" &&
    `
    padding: ${SPACING.xs + SPACING.xxs}px ${SPACING.sm + SPACING.xs}px;
  `}

  background: var(--color-background-2);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  cursor: pointer;
  font-size: var(--text-base);
  color: var(--color-text-default);
  transition: ${ANIMATION.transitions.all};
  min-width: 100%;
  justify-content: center;

  &:hover {
    background: var(--color-background-2);
    border-color: var(--color-primary);
  }

  &:active {
    transform: translateY(1px);
    transition: transform ${ANIMATION.duration.instant}
      ${ANIMATION.easing.easeOut};
  }

  &:disabled {
    &:hover {
      background: var(--color-background-2);
      border-color: var(--color-border);
    }
  }

  @media ${QUERIES.tabletAndUp} {
    min-width: 160px;
  }
`;

// Simplified menu container - no positioning styles since it's handled by portal
export const DropdownMenu = styled.div<{ $isOpen?: boolean }>`
  background: var(--color-background-2);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  min-width: 200px;

  ${({ $isOpen }) =>
    $isOpen &&
    css`
      animation: ${fadeInScale} ${ANIMATION.duration.fast}
        ${ANIMATION.easing.easeOut};
    `}
`;

// Menu item base styles
const menuItemBase = css`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm}px;
  width: 100%;
  padding: ${SPACING.sm + SPACING.xs}px ${SPACING.md}px;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
  font-size: var(--text-base);
  color: var(--color-text-default);
  transition: background-color ${ANIMATION.duration.fast}
    ${ANIMATION.easing.easeOut};

  /* Icon alignment */
  > span {
    display: inline-flex;
    align-items: center;
    justify-content: center;

    /* Ensure MUI icons are vertically centered */
    svg {
      vertical-align: middle;
      display: block;
    }
  }

  &:hover {
    background: var(--color-primary);
    color: var(--color-white);
  }

  /* Inset ring: the surrounding menu clips an outer one. */
  &:focus-visible {
    outline: 2px solid var(--color-primary);
    outline-offset: -2px;
    box-shadow: none;
    background: var(--color-hover-overlay);
  }

  &:active {
    background: var(--color-border);
  }

  &:disabled {
    opacity: 0.5;
    cursor: revert;

    &:hover {
      background: transparent;
    }
  }
`;

// Regular menu item
export const DropdownMenuItem = styled(BaseButton)`
  ${menuItemBase}
`;

export const MenuItemText = styled.span`
  && {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: ${SPACING.xxs}px;
  }
`;

export const MenuItemReason = styled.span`
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
`;

// Destructive menu item variant
export const DropdownMenuItemDestructive = styled(BaseButton)`
  ${menuItemBase}
  color: var(--color-error);

  &:hover {
    background: var(--color-error-border);
    color: var(--color-error);
  }

  /* Inset ring: the surrounding menu clips an outer one. */
  &:focus-visible {
    outline: 2px solid var(--color-error);
    outline-offset: -2px;
    box-shadow: none;
    background: var(--color-error-background-2);
  }

  &:active {
    background: var(--color-status-error-background);
  }
`;

// Menu separator
export const DropdownSeparator = styled.hr`
  border: none;
  height: 1px;
  background: var(--color-border);
  margin: ${SPACING.xs}px 0;
`;

// Chevron icon wrapper with rotation animation
export const ChevronIcon = styled.span<{ $isOpen?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform ${ANIMATION.duration.normal} ${ANIMATION.easing.easeOut};

  ${({ $isOpen }) =>
    $isOpen &&
    css`
      transform: rotate(180deg);
    `}
`;
